/**
 * Family connections — the pure rules the surfaces render from (ZOY-665).
 *
 * Everything here reads the Clinic Backend contract as it is: statuses are
 * Auth's words mirrored by the projection, actions follow the row's kind and
 * status (the backend refuses anything else with FAMILY_LINK_INVALID_STATE),
 * and the invitable numbers are the record's OWN numbers — the contact mobile
 * and the active caregivers — because the backend refuses any other number.
 * Nothing is matched by name anywhere.
 */

import type { ApiError } from '@/core/api/errors';
import type { NoticeKind } from '@/ui/kit';
import type { PatientView } from '@/core/clinic/patientsApi';
import {
  FamilyErrorCode,
  type FamilyLinkKind,
  type FamilyLinkView,
  type FamilyStatus,
} from '@/core/clinic/familyApi';

export type ChipTone = NoticeKind | 'quiet';

/** The status chip's tone: pending = attention, accepted = live, everything closed = quiet. */
export function statusTone(status: FamilyStatus, localState: string): ChipTone {
  if (localState === 'failed') return 'err';
  if (localState === 'creating') return 'quiet';
  switch (status) {
    case 'pending':
      return 'warn';
    case 'accepted':
      return 'ok';
    case 'declined':
      return 'err';
    default:
      return 'quiet';
  }
}

/** The catalog key for a row's status — `family.st.<status>`, or the raw word for an unknown one. */
export function statusLabelKey(status: FamilyStatus): string {
  return `family.st.${status}`;
}

export interface LinkActions {
  resend: boolean;
  cancel: boolean;
  end: boolean;
  accept: boolean;
  decline: boolean;
}

const NONE: LinkActions = {
  resend: false,
  cancel: false,
  end: false,
  accept: false,
  decline: false,
};

/**
 * Which actions a row admits. `canManage` is the families.manage key; without
 * it every action is absent (never disabled). A row still `creating` (its
 * Auth answer pending) or `failed` offers nothing — the operator's next invite
 * replays it on the backend's write path.
 */
export function linkActions(link: FamilyLinkView, canManage: boolean): LinkActions {
  if (!canManage || link.localState !== 'synced') return NONE;
  const pending = link.status === 'pending';
  switch (link.kind) {
    case 'invitation':
      return { ...NONE, resend: pending, cancel: pending };
    case 'request':
      return { ...NONE, accept: pending, decline: pending };
    case 'connection':
      return { ...NONE, end: link.status === 'accepted' };
  }
}

export interface InviteCandidate {
  mobile: string;
  /** Who this number belongs to on the record — the contact mobile, or a caregiver's name. */
  label: string;
  kind: 'contact' | 'caregiver';
}

/**
 * The numbers an invitation may address: the record's contact mobile and
 * every active caregiver's mobile, deduplicated (a caregiver often IS the
 * contact number). Order: contact first.
 */
export function inviteCandidates(patient: PatientView, contactLabel: string): InviteCandidate[] {
  const out: InviteCandidate[] = [];
  const seen = new Set<string>();
  const push = (c: InviteCandidate) => {
    if (c.mobile.trim() === '' || seen.has(c.mobile)) return;
    seen.add(c.mobile);
    out.push(c);
  };
  push({ mobile: patient.contactMobile, label: contactLabel, kind: 'contact' });
  for (const cg of patient.caregivers ?? []) {
    if (cg.removedAt === null) push({ mobile: cg.mobile, label: cg.name, kind: 'caregiver' });
  }
  return out;
}

/** Only a registered patient can be invited (backend: FAMILY_PATIENT_NOT_REGISTERED). */
export function canInvite(
  panelState: 'guest' | 'registered' | 'merged',
  canManage: boolean,
): boolean {
  return canManage && panelState === 'registered';
}

/**
 * A row in the panel sits in one of three groups: the live relationship, the
 * open items that still need someone to act, and the closed history.
 */
export type PanelGroup = 'current' | 'open' | 'history';

export function panelGroup(link: FamilyLinkView): PanelGroup {
  if (link.kind === 'connection') return link.status === 'accepted' ? 'current' : 'history';
  return link.status === 'pending' || link.localState === 'creating' ? 'open' : 'history';
}

/** Newest first — by Auth's own timestamps when it has them, else the row's. */
export function byRecency(a: FamilyLinkView, b: FamilyLinkView): number {
  const at = (l: FamilyLinkView) =>
    Date.parse(l.acceptedAt ?? l.authUpdatedAt ?? l.updatedAt ?? l.createdAt);
  return at(b) - at(a);
}

/**
 * The catalog key that names a refused family action for the operator. The
 * backend's typed codes are the vocabulary; anything else is the generic
 * refusal, and a 429 carries `retryAfterSeconds` the caller shows separately.
 */
export function familyErrorKey(err: ApiError): string {
  switch (err.code) {
    case FamilyErrorCode.PatientAlreadyConnected:
      return 'family.err.alreadyConnected';
    case FamilyErrorCode.PatientNotRegistered:
      return 'family.err.notRegistered';
    case FamilyErrorCode.MobileNotOnRecord:
      return 'family.err.mobileNotOnRecord';
    case FamilyErrorCode.MobileNotCanonical:
      return 'family.err.mobileNotCanonical';
    case FamilyErrorCode.QuotaExceeded:
      return 'family.err.quota';
    case FamilyErrorCode.LinkInvalidState:
      return 'family.err.invalidState';
    case FamilyErrorCode.LinkNotFound:
      return 'family.err.notFound';
    case FamilyErrorCode.AuthRefused:
      return 'family.err.authRefused';
    case FamilyErrorCode.ClinicInactive:
      return 'family.err.clinicInactive';
    case FamilyErrorCode.Unavailable:
      return 'family.err.unavailable';
    default:
      return err.status === 403 ? 'family.err.authRefused' : 'family.err.generic';
  }
}

/** The `kind` filter as a server filter — `all` sends nothing. */
export function kindFilter(kind: FamilyLinkKind | 'all') {
  return kind === 'all' ? [] : [{ field: 'kind', op: 'eq' as const, value: kind }];
}
